import {
  DatePicker,
  DatePickerProps,
} from "@jugl-web/ui-components/cross-platform/DatePicker";
import {
  TaskPropertyButton,
  TaskPropertyButtonProps,
} from "@jugl-web/ui-components/cross-platform/tasks/TaskPropertyButton";
import { MobileDrawer } from "@jugl-web/ui-components/mobile";
import { DueDatePickerPopover } from "@jugl-web/ui-components/web/DueDatePickerPopover";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { FC, useState } from "react";
import { getDueDateInDays } from "../../../../utils";
import { ReactComponent as DueDateIcon } from "../../assets/due-date.svg";
import { FieldComponentProps } from "../../types";

export const DueDateField: FC<FieldComponentProps<"dueDate">> = ({
  date,
  displayAs = "date",
  isReadonly,
  isLabelHidden,
  isDateEditable = true,
  onChange,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isFieldVisible = !!date || !isReadonly;

  const { t } = useTranslations();
  const { formatDateTimeLabel } = useFormattedDate();

  const { isMobile } = useAppVariant();

  const label = (() => {
    if (!date) {
      return isLabelHidden
        ? null
        : t({
            id: "task-templates-page.due-date",
            defaultMessage: "Due date",
          });
    }

    if (displayAs === "days") {
      return t(
        {
          id: "tasks-page.due-in-days",
          defaultMessage:
            "Due in: {days} {days, plural, one {day} other {days}}",
        },
        { days: getDueDateInDays(date) }
      );
    }

    const dueDateTimeLabel = formatDateTimeLabel(date);

    return isLabelHidden
      ? dueDateTimeLabel
      : t(
          { id: "tasks-page.due-to-date", defaultMessage: "Due to: {date}" },
          { date: dueDateTimeLabel }
        );
  })();

  if (!isFieldVisible) {
    return null;
  }

  const commonButtonProps: TaskPropertyButtonProps = {
    isDisabled: isReadonly,
    startIcon: <DueDateIcon className={cx(date && "text-primary")} />,
    className: cx(date && "bg-primary-50 text-primary-900"),
    children: label,
  };

  const title = t({
    id: "tasks-page.due-date-popup-title",
    defaultMessage: "Select task due date",
  });

  const commonDatePickerProps: DatePickerProps = {
    initialDate: date || undefined,
    minDate: new Date(),
    isDateRemovable: true,
    removeDateLabel: t({
      id: "tasks-page.remove-due-date",
      defaultMessage: "Remove due date",
    }),
    dateTransformation: "endOfDay",
    onSubmit: (dueDate) => onChange?.(dueDate),
  };

  if (isMobile) {
    return (
      <>
        <TaskPropertyButton
          onClick={() => setIsDialogOpen(true)}
          {...commonButtonProps}
        />
        <MobileDrawer
          isOpen={isDialogOpen}
          header={{ title }}
          animateHeight
          onClose={() => setIsDialogOpen(false)}
        >
          <MobileDrawer.Content>
            <DatePicker
              onClose={() => setIsDialogOpen(false)}
              {...commonDatePickerProps}
            />
          </MobileDrawer.Content>
        </MobileDrawer>
      </>
    );
  }

  return (
    <DueDatePickerPopover
      placement="bottom-start"
      renderTrigger={({ Trigger, triggerRef }) => (
        <Trigger
          ref={triggerRef}
          as={TaskPropertyButton}
          {...commonButtonProps}
        />
      )}
      initialDate={date || undefined}
      isDateEditable={isDateEditable}
      onSubmit={(dueDate) => onChange?.(dueDate)}
    />
  );
};
